import { createClient, dedupExchange, fetchExchange, subscriptionExchange, OperationResult } from '@urql/core'
import { createClient as createWSClient } from 'graphql-ws'
import { pipe, subscribe as wonkaSubscribe } from 'wonka'
import { LongPollClient } from './longPoll'
import { areWebsocketsSupported, whileTestingWebsocketSupport } from './supportsGraphQLTransportWS'
import { CONDUIT, RAILS } from './endpoints'
import { retryExchange } from '@urql/exchange-retry'
import { setupCsrfTokenExchange } from './csrfTokenExchange'
import { dispatch as dispatchTransportEvent, TRANSPORT_EVENTS } from './transportEvents'

let didSuccessfullyConnect = false
const wsClient = createWSClient({
  url: CONDUIT,
  lazyCloseTimeout: 5000,
  on: {
    connected: (e) => {
      dispatchTransportEvent(TRANSPORT_EVENTS.CONNECTED, { transport: 'websocket', reconnect: didSuccessfullyConnect, originalEvent: e })
      didSuccessfullyConnect = true
    },
    opened: (e) => dispatchTransportEvent(TRANSPORT_EVENTS.OPENED, { transport: 'websocket', originalEvent: e }),
    closed: (e) => dispatchTransportEvent(TRANSPORT_EVENTS.CLOSED, { transport: 'websocket', originalEvent: e }),
    error: (e) => dispatchTransportEvent(TRANSPORT_EVENTS.ERROR, { transport: 'websocket', error: e })
  }
})

let longPollClient: LongPollClient
const client = createClient({
  url: RAILS,
  fetchOptions: {
    credentials: 'include'
  },
  exchanges: [
    dedupExchange,
    setupCsrfTokenExchange(),
    retryExchange({}),
    fetchExchange,
    subscriptionExchange({
      forwardSubscription (operation) {
        return {
          subscribe: (sink) => {
            if (areWebsocketsSupported()) {
              const dispose = wsClient.subscribe(operation, sink)
              return { unsubscribe: dispose }
            } else {
              if (!longPollClient) longPollClient = new LongPollClient()
              const dispose = longPollClient.subscribe(operation, sink)
              return { unsubscribe: dispose }
            }
          }
        }
      }
    })
  ]
})

interface QueryOptions {
  query: Parameters<typeof client.query>[0],
  variables?: Parameters<typeof client.query>[1],
  context?: Parameters<typeof client.query>[2]
}

export async function query ({ query, variables, context }: QueryOptions): Promise<OperationResult> {
  const result = await client.query(query, variables, context).toPromise()
  if (result.error) throw result.error
  return result
}

interface MutationOptions {
  query: Parameters<typeof client.mutation>[0],
  variables?: Parameters<typeof client.mutation>[1],
  context?: Parameters<typeof client.mutation>[2]
}

export async function mutation ({ query, variables, context }: MutationOptions): Promise<OperationResult> {
  const result = await client.mutation(query, variables, context).toPromise()
  if (result.error) throw result.error
  return result
}

interface SubscriptionOptions {
  query: Parameters<typeof client.subscription>[0],
  variables?: Parameters<typeof client.subscription>[1],
  context?: Parameters<typeof client.subscription>[2]
}

export async function subscribe ({ query, variables, context }: SubscriptionOptions, handler: Parameters<typeof wonkaSubscribe>[0]): Promise<{ unsubscribe: () => void }> {
  await whileTestingWebsocketSupport

  const { unsubscribe } = pipe(
    client.subscription(query, variables, context),
    wonkaSubscribe(handler)
  )

  return { unsubscribe }
}
